<template>
  <div class="credit-market-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>积分市场管理（解债后积分交易）</span>
          <el-tag type="success">平台手续费: 5%</el-tag>
        </div>
      </template>
      
      <el-alert type="info" :closable="false" style="margin-bottom: 20px">
        <template #title>
          <strong>积分市场</strong>：用户解债完成后，可将剩余积分在市场上交易变现
        </template>
      </el-alert>

      <el-tabs v-model="activeTab">
        <el-tab-pane label="在售列表" name="active">
          <el-table :data="listings" border stripe>
            <el-table-column prop="id" label="ID" width="60" />
            <el-table-column prop="sellerName" label="卖家" width="120" />
            <el-table-column prop="creditType" label="积分类型" width="100">
              <template #default="{ row }">
                <el-tag :type="creditTypeMap[row.creditType]?.type">
                  {{ creditTypeMap[row.creditType]?.label }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="amount" label="数量" width="100" />
            <el-table-column prop="price" label="单价" width="100">
              <template #default="{ row }">
                {{ row.price }}元/分
              </template>
            </el-table-column>
            <el-table-column prop="totalPrice" label="总价" width="120">
              <template #default="{ row }">
                <el-tag type="warning">{{ row.amount * row.price }}元</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="100">
              <template #default="{ row }">
                <el-tag :type="statusMap[row.status]?.type">
                  {{ statusMap[row.status]?.label }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="createdAt" label="发布时间" width="180" />
            <el-table-column label="操作" width="120">
              <template #default="{ row }">
                <el-button type="danger" size="small" @click="cancelListing(row.id)">下架</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
        
        <el-tab-pane label="交易记录" name="history">
          <el-table :data="transactions" border stripe>
            <el-table-column prop="id" label="ID" width="60" />
            <el-table-column prop="sellerName" label="卖家" width="120" />
            <el-table-column prop="buyerName" label="买家" width="120" />
            <el-table-column prop="amount" label="数量" width="100" />
            <el-table-column prop="price" label="单价" width="100" />
            <el-table-column prop="totalPrice" label="总价" width="100" />
            <el-table-column prop="platformFee" label="平台手续费" width="100">
              <template #default="{ row }">
                <el-tag type="danger">{{ row.platformFee }}元</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="createdAt" label="交易时间" width="180" />
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <!-- 市场统计 -->
    <el-row :gutter="20" style="margin-top: 20px">
      <el-col :span="6">
        <el-card shadow="hover">
          <el-statistic title="今日交易量" :value="stats.todayVolume">
            <template #suffix>分</template>
          </el-statistic>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover">
          <el-statistic title="今日交易额" :value="stats.todayAmount" prefix="¥">
            <template #suffix>元</template>
          </el-statistic>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover">
          <el-statistic title="今日平台收入" :value="stats.todayFee" prefix="¥">
            <template #suffix>元</template>
          </el-statistic>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover">
          <el-statistic title="当前在售数量" :value="stats.activeListings">
            <template #suffix>笔</template>
          </el-statistic>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { creditMarketApi } from '@/api/modules/debt-neutralization';
import { ElMessage, ElMessageBox } from 'element-plus';

const activeTab = ref('active');
const listings = ref([]);
const transactions = ref([]);
const stats = reactive({
  todayVolume: 0,
  todayAmount: 0,
  todayFee: 0,
  activeListings: 0,
});

const creditTypeMap = {
  cash_credit: { label: '现金分', type: 'success' },
  debt_resolution: { label: '解债分', type: 'warning' },
};

const statusMap = {
  active: { label: '在售', type: 'success' },
  sold: { label: '已售', type: 'info' },
  cancelled: { label: '已下架', type: 'danger' },
};
const loadListings = async () => {
  try {
    const res = await creditMarketApi.getListings({ status: 'active' });
    if (res.code === 200) {
      listings.value = res.data.list;
      stats.activeListings = listings.value.length;
    }
  } catch (error) {
    ElMessage.error('加载失败');
  }
};

const cancelListing = async (listingId) => {
  try {
    await ElMessageBox.confirm('确定要下架该积分吗？', '提示', {
      type: 'warning',
    });
    const res = await creditMarketApi.cancel({ listingId });
    if (res.code === 200) {
      ElMessage.success('下架成功');
      loadListings();
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('操作失败');
    }
  }
};

onMounted(() => {
  loadListings();
});
</script>

<style scoped>
.credit-market-management {
  padding: 20px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
